import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getKSectionVO,
    saveKSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    K0_PAGE,
    K1_PAGE,
    K_1_PAGE,
    K_PAGE_TITLES,
    K_SECTION_PAGES,
    K1Page,
    KDetailPage,
    KProgrammePage,
    KSectionPageCode,
} from '@/pages/components/audit-program/k-section/constants';
import K1LeadView from '@/pages/components/audit-program/k-section/K1LeadView';
import KDetailView from '@/pages/components/audit-program/k-section/KDetailView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type KSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: KSectionPageCode;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function KSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'K0',
}: KSectionViewProps) {
    const [pageKey, setPageKey] = useState<KSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('K');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getKSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of K_SECTION_PAGES) {
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load K Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: KSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (!dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveKSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const k0Content = (getContent('K0') ?? K0_PAGE) as KProgrammePage;
    const k1Content = (getContent('K1') ?? K1_PAGE) as K1Page;
    const k1uContent = (getContent('K_1') ?? K_1_PAGE) as KDetailPage;

    const tabs = useMemo(() => K_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={K_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'K0' ? (
                <G0ProgrammeView
                    header={header}
                    page={k0Content}
                    refCode="K0"
                    values={pageData.K0 ?? {}}
                    onChange={(k, v) => handleFieldChange('K0', k, v)}
                />
            ) : code === 'K1' ? (
                <K1LeadView
                    header={header}
                    page={k1Content}
                    values={pageData.K1 ?? {}}
                    onChange={(k, v) => handleFieldChange('K1', k, v)}
                />
            ) : (
                <KDetailView
                    header={header}
                    page={k1uContent}
                    refCode="K_1"
                    values={pageData.K_1 ?? {}}
                    onChange={(k, v) => handleFieldChange('K_1', k, v)}
                />
            )
        ) : null,
    })), [header, pageData, handleFieldChange, k0Content, k1Content, k1uContent]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    K Section — Bank balances and cash
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={!dirty[pageKey] || saving}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as KSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
